import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useForm } from 'react-hook-form';
import { fetchSchemasAsync, clearSchemas, EMPTY_FILTERS } from '../../../redux/flows/schemasSlice';

const DEBOUNCE_MS = 500;

/**
 * Owns the schema-filter form: the react-hook-form instance, the initial fetch
 * (+ cleanup on unmount), and a single debounced refetch whenever any filter
 * changes. Mirrors the locatorManagement `useLocatorsSearch` pattern. Returns
 * the form (for <SchemaFilters/>) and a clear handler.
 */
export default function useSchemaFilters() {
  const dispatch = useDispatch();
  const form = useForm({ defaultValues: EMPTY_FILTERS });
  const values = form.watch();

  // Initial load + cleanup.
  useEffect(() => {
    dispatch(fetchSchemasAsync({ filters: EMPTY_FILTERS }));
    return () => {
      dispatch(clearSchemas());
    };
  }, [dispatch]);

  // Stable dep keys: array fields keyed by joined contents (so identity-only
  // changes don't refetch), strings trimmed (so trailing whitespace doesn't).
  const prioritiesKey = values.priorities.join(',');
  const eventTypesKey = values.eventTypes.join(',');
  const nameKey = values.searchEventName.trim();
  const titleKey = values.searchEventTitle.trim();

  // Debounced refetch on filter change. Skips the first run so it doesn't
  // double-fetch on mount (the effect above already did the initial load).
  const isFirstRun = useRef(true);
  useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false;
      return;
    }
    const timer = setTimeout(() => {
      dispatch(
        fetchSchemasAsync({
          filters: {
            priorities: values.priorities,
            eventTypes: values.eventTypes,
            searchEventName: nameKey,
            searchEventTitle: titleKey,
          },
        }),
      );
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prioritiesKey, eventTypesKey, nameKey, titleKey, dispatch]);

  const handleClear = () => form.reset(EMPTY_FILTERS);

  return { form, handleClear };
}
